/* oxlint-disable sorare/enforce-monetary-reward-disclaimer -- Non-reward currency (wallet/market/JONUM limits/etc.) — disclaimer does not apply. */
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { SupportedCurrency } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  BodyS,
  HeadlineL,
  LabelM,
} from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { SelectWithCustomInput } from '#src/components/form/SelectWithCustomInput/index.tsx';
import {
  MAX_HOURS_PER_WEEK,
  SPEND_LIMIT_VALUES,
  TIME_LIMIT_VALUES,
} from '#src/components/jonum/constants.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';

import {
  UPDATE_JONUM_PLAYTIME_LIMIT,
  UPDATE_JONUM_SPENDING_LIMIT,
} from '../mutations.ts';

const Content = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
`;

const Actions = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
  margin-top: var(--unit);
`;

const ConfirmationContent = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  align-items: center;
  text-align: center;
`;

type LimitType = 'playtime' | 'spending';

type Props = {
  type: LimitType;
  open: boolean;
  onClose: () => void;
  currentLimit: string;
};

export const EditLimitDialog = ({
  type,
  open,
  onClose,
  currentLimit,
}: Props) => {
  const { formatMessage, formatNumber } = useIntl();
  const [value, setValue] = useState(currentLimit);
  const [confirmed, setConfirmed] = useState(false);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);

  const [updatePlaytime] = useMutation(UPDATE_JONUM_PLAYTIME_LIMIT, {
    showErrorsWithSnackNotification: true,
  });
  const [updateSpending] = useMutation(UPDATE_JONUM_SPENDING_LIMIT, {
    showErrorsWithSnackNotification: true,
  });

  const handleConfirm = async () => {
    if (type === 'playtime') {
      const minutes = Number(value) * 60;
      const result = await updatePlaytime({
        variables: { input: { playtimeLimitWeekly: minutes } },
      });
      const effectiveAt =
        result.data?.setJonumPlaytimeLimit?.pendingEffectiveAt;
      setPendingDate(effectiveAt ? new Date(effectiveAt) : null);
    } else {
      const result = await updateSpending({
        variables: {
          input: {
            spendingLimitWeekly: {
              amount: String(Number(value) * 100),
              currency: 'EUR' as SupportedCurrency,
            },
          },
        },
      });
      const effectiveAt =
        result.data?.setJonumSpendingLimit?.pendingEffectiveAt;
      setPendingDate(effectiveAt ? new Date(effectiveAt) : null);
    }
    setConfirmed(true);
  };

  const handleClose = () => {
    setValue(currentLimit);
    setConfirmed(false);
    setPendingDate(null);
    onClose();
  };

  const isPlaytime = type === 'playtime';
  const options = useMemo(
    () =>
      isPlaytime
        ? TIME_LIMIT_VALUES.map(v => ({
            value: String(v),
            label: formatMessage(
              {
                defaultMessage: '{count, plural, one {# hour} other {# hours}}',
              },
              { count: v }
            ),
          }))
        : SPEND_LIMIT_VALUES.map(v => ({
            value: String(v),
            label: formatNumber(v, {
              style: 'currency',
              currency: 'EUR',
              maximumFractionDigits: 0,
            }),
          })),
    [isPlaytime, formatMessage, formatNumber]
  );
  const max = isPlaytime ? MAX_HOURS_PER_WEEK : Infinity;
  const unit = isPlaytime ? formatMessage({ defaultMessage: 'hours' }) : '€';

  const isValid = value !== '' && Number(value) >= 1 && Number(value) <= max;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      hideHeader
      maxWidth="xs"
      fullWidth
    >
      {confirmed ? (
        <ConfirmationContent>
          <HeadlineL color="var(--c-white)" bold>
            <FormattedMessage defaultMessage="Change saved" />
          </HeadlineL>
          <BodyS color="var(--c-nd-600)">
            {pendingDate ? (
              <FormattedMessage defaultMessage="This change will be effective in 48h." />
            ) : (
              <FormattedMessage defaultMessage="This change is effective immediately." />
            )}
          </BodyS>
          <Actions>
            <Button
              size="medium"
              color="primary"
              onClick={handleClose}
              fullWidth
            >
              <FormattedMessage defaultMessage="Continue" />
            </Button>
          </Actions>
        </ConfirmationContent>
      ) : (
        <Content>
          <HeadlineL color="var(--c-white)" bold>
            <FormattedMessage defaultMessage="Play at your own pace" />
          </HeadlineL>
          <BodyM color="var(--c-nd-600)">
            {isPlaytime ? (
              <FormattedMessage defaultMessage="You can adjust your play time limit at any time. If you decrease it, the change is immediate. If you increase it, it will be effective after 48 hours. Your reset date remains unchanged." />
            ) : (
              <FormattedMessage defaultMessage="You can adjust your spending limit at any time. If you decrease it, the change is immediate. If you increase it, it will be effective after 48 hours. Your reset date remains unchanged." />
            )}
          </BodyM>
          <LabelM bold color="var(--c-white)">
            {isPlaytime ? (
              <FormattedMessage defaultMessage="Duration" />
            ) : (
              <FormattedMessage defaultMessage="Amount" />
            )}
          </LabelM>
          <SelectWithCustomInput
            options={options}
            value={value}
            onChange={setValue}
            min={1}
            max={max}
            unit={unit}
          />
          <Actions>
            <Button
              size="medium"
              color="primary"
              onClick={handleConfirm}
              disabled={!isValid}
              fullWidth
            >
              <FormattedMessage defaultMessage="Confirm change" />
            </Button>
            <Button
              size="medium"
              color="transparent"
              onClick={handleClose}
              fullWidth
            >
              <FormattedMessage defaultMessage="Cancel" />
            </Button>
          </Actions>
        </Content>
      )}
    </Dialog>
  );
};
